// The Google Ads specialist dashboard.
//
// Built on the Google Ads → BigQuery Data Transfer (the `google_ads` view layer),
// not Zernio: impression share, lost-IS budget-vs-rank, bid strategy, target
// ROAS/CPA and search terms are unreachable through Zernio's normalised
// cross-platform tree.
//
// The page is one scope. The filter bar and the drill focus feed EVERY panel
// (KPIs, trend, campaign-type split, competitiveness, tree, search terms) from
// the same filter object, so drilling into a campaign re-scopes the whole page
// rather than just the table.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownToLine, RefreshCw } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { presetToRange, previousRange } from '../../lib/reportingDateRange';
import type { DateRange } from '../../lib/reportingDateRange';
import type { ReportingDatePreset } from '../../types/reporting';
import type {
  GoogleAdsFilters,
  GoogleAdsFocus,
  GoogleAdsLevel,
  GoogleAdsMetricKey,
  GoogleAdsTreeRow,
} from '../../types/googleAdsReporting';
import {
  getGoogleAdsChannelBreakdown,
  getGoogleAdsFilterOptions,
  getGoogleAdsSearchTerms,
  getGoogleAdsSummary,
  getGoogleAdsTree,
  toQueryString,
} from './api';
import { GoogleAdsFilterBar } from './GoogleAdsFilterBar';
import { MetricPicker } from './GoogleAdsKpis';
import {
  GoogleAdsBigChart,
  GoogleAdsKpiTile,
  GoogleChartsMenu,
} from './GoogleAdsCharts';
import {
  ReportingChartGrid,
  type ChartPanel,
} from '../reporting/ReportingChartGrid';
import {
  AddWidgetButton,
  buildWidgetPanels,
  type WidgetDataSource,
} from '../reporting/ReportingWidgets';
import { ReportingViewActions } from '../reporting/ReportingViewActions';
import type { ReportingWidget } from '../../lib/reporting/widgets';
import { canvasPanelKey, mergeStoredLayout } from '../../lib/reporting/chartLayout';
import { GoogleAdsTrend } from './GoogleAdsTrend';
import {
  buildGoogleTrendSeries,
  googleMetricDelta,
  googleMetricTone,
} from './googleTrendSeries';
import type { TrendChartData } from '../reporting/trendSeries';
import { CompetitivenessBlock } from './CompetitivenessBlock';
import { ChannelBreakdown } from './ChannelBreakdown';
import { GoogleAdsTree, type GoogleAdsSortKey } from './GoogleAdsTree';
import { GOOGLE_ADS_ATTRIBUTES, type GoogleAdsAttributeKey } from './attributeColumns';
import { SEARCH_TERMS_ANCHOR, SearchTermsPanel } from './SearchTermsPanel';
import { breadcrumbs, focusForRow } from './focusNav';
import { LevelChips } from './LevelChips';
import { GoogleTablesMenu } from './ColumnsMenu';
import {
  GOOGLE_ADS_GRID_KEY,
  googleAdsCanvasPanelKeys,
  decodeGoogleAdsViewState,
  encodeGoogleAdsViewState,
  tileStyleFor,
  type GoogleAdsViewState,
} from './googleAdsViewPrefs';
import { toggleLevel } from '../../lib/googleAdsLevels';
import { GOOGLE_ADS_METRICS, metricDef, metricScalar } from '../../lib/googleAdsMetrics';
import {
  buildGoogleDimensionalData,
  GOOGLE_WIDGET_DIMENSIONS,
} from './googleWidgetDimensions';
import type { ChartStyle } from '../reporting/chartPrefs';
import { useCurrentUser } from '../auth';
import {
  ConversionNote,
  DisplayCurrencySelect,
  getFxRates,
} from '../reporting';
import { convertGoogleMetrics } from './googleCurrency';
import { normalizeDisplayCurrency } from '../../lib/reporting/currencyConversion';
import { patchMyPreferences } from '../accounts';
import {
  NOT_MEASURED,
  formatCount,
  formatMetric,
  isMetricUnmeasured,
  formatMoneyAmounts as formatCurrencyAmounts,
} from './metricFormat';

const DEFAULT_PRESET: ReportingDatePreset = 'past_30';

export function GoogleAdsPage(): React.JSX.Element {
  const [preset, setPreset] = useState<ReportingDatePreset>(DEFAULT_PRESET);
  const [custom, setCustom] = useState<DateRange>(() => presetToRange(DEFAULT_PRESET, new Date()));
  // A `?accountId=` deep link (the Advertising Home's alert/book rows) seeds
  // the Account filter ONCE at mount — lazy read, never re-applied.
  const [filters, setFilters] = useState<GoogleAdsFilters>(() => {
    const seeded = new URLSearchParams(window.location.search).get('accountId');
    return {
      ...presetToRange(DEFAULT_PRESET, new Date()),
      ...(seeded ? { accountIds: [seeded] } : {}),
    };
  });
  const [focus, setFocus] = useState<GoogleAdsFocus | null>(null);
  // Names of the entities drilled through, so breadcrumbs read as names, not ids.
  const [focusNames, setFocusNames] = useState<{
    location?: string | null;
    campaign?: string | null;
    adGroup?: string | null;
  }>({});


  // The tree LEVELS + metric COLUMNS persist through the shared gridPreferences
  // store (the same mechanism the accounts grid and the Meta explorer use), so a
  // user's chosen grouping is still there next time. Seeded ONCE from the stored
  // prefs — re-applying on every /me refetch would stomp a selection the user
  // just made while its save was still in flight.
  const user = useCurrentUser();
  const qc = useQueryClient();
  const [view, setView] = useState<GoogleAdsViewState>(() =>
    decodeGoogleAdsViewState(user.gridPreferences?.[GOOGLE_ADS_GRID_KEY]),
  );

  const saveView = useMutation({
    mutationFn: (next: GoogleAdsViewState) =>
      patchMyPreferences(GOOGLE_ADS_GRID_KEY, encodeGoogleAdsViewState(next)),
    onSuccess: (user) => qc.setQueryData(['me'], user),
  });

  const commitView = useCallback(
    (next: GoogleAdsViewState) => {
      // Apply locally first — the table must not wait on a preferences round trip.
      setView(next);
      saveView.mutate(next, {
        onError: (err) => console.error('Failed to save Google Ads view preferences', err),
      });
    },
    [saveView],
  );

  const onToggleLevel = useCallback(
    (level: GoogleAdsLevel) => {
      const levels = toggleLevel(view.levels, level);
      if (levels.length === view.levels.length && levels.every((l, i) => l === view.levels[i])) {
        return; // the at-least-one guard refused; nothing to save
      }
      // A drill path can point at a level that is no longer shown, which would
      // scope the page to something the table can't display — so changing the
      // grouping clears the focus, exactly as changing a filter does.
      setFocus(null);
      setFocusNames({});
      commitView({ ...view, levels });
    },
    [view, commitView],
  );

  const columnMetrics = view.columns;
  const setColumnMetrics = useCallback(
    (columns: GoogleAdsMetricKey[]) => commitView({ ...view, columns }),
    [view, commitView],
  );
  const setKpiMetrics = useCallback(
    (kpis: GoogleAdsMetricKey[]) => commitView({ ...view, kpis }),
    [view, commitView],
  );
  const setAttributeColumns = useCallback(
    (attributes: GoogleAdsAttributeKey[]) => commitView({ ...view, attributes }),
    [view, commitView],
  );
  const setTileStyle = useCallback(
    (key: GoogleAdsMetricKey, style: ChartStyle) =>
      commitView({ ...view, tileStyles: { ...view.tileStyles, [key]: style } }),
    [view, commitView],
  );
  const setTrendMetrics = useCallback(
    (trendMetrics: GoogleAdsMetricKey[]) => commitView({ ...view, trendMetrics }),
    [view, commitView],
  );
  // Sort is SESSION state, deliberately not persisted — persisting it belongs
  // with the save-model work, not here.
  const [sortKey, setSortKey] = useState<GoogleAdsSortKey>('cost');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // The ONE scope object every panel queries with.
  // The ONE scope object every panel queries with. `levels` only affects the
  // TREE — the KPIs, trend and search terms follow the filters + focus, so
  // changing the grouping never changes the numbers above the table.
  const scoped: GoogleAdsFilters = useMemo(
    () => ({ ...filters, focus, levels: view.levels }),
    [filters, focus, view.levels],
  );
  const key = toQueryString(scoped);

  const options = useQuery({
    queryKey: ['google-ads-filter-options'],
    queryFn: getGoogleAdsFilterOptions,
    staleTime: 5 * 60 * 1000,
  });
  // ⚠️ The SAME query key the Meta dashboard uses, so both share one cached read
  // of one rate table and structurally cannot convert at different rates.
  const fx = useQuery({
    queryKey: ['reporting-fx-rates'],
    queryFn: getFxRates,
    staleTime: 30 * 60 * 1000,
  });
  const summary = useQuery({
    queryKey: ['google-ads-summary', key],
    queryFn: () => getGoogleAdsSummary(scoped),
  });
  const channels = useQuery({
    queryKey: ['google-ads-channels', key],
    queryFn: () => getGoogleAdsChannelBreakdown(scoped),
  });
  const tree = useQuery({
    queryKey: ['google-ads-tree', key],
    queryFn: () => getGoogleAdsTree(scoped),
  });
  const terms = useQuery({
    queryKey: ['google-ads-terms', key],
    queryFn: () => getGoogleAdsSearchTerms(scoped),
  });

  // ⚠️ THE PREVIOUS EQUIVALENT PERIOD IS FETCHED LAZILY, and only for the widget
  // scorecards' "compare to previous period". It is the SAME /summary endpoint
  // over a shifted range (exactly what the Meta page does for its tile deltas),
  // so it adds no data path — but this dashboard has no other use for it, and
  // paying for a second full summary on every page load to serve a comparison
  // nobody asked for would be wrong. No widget wants a comparison → no request.
  const wantsComparison = view.widgets.some((w) => w.showComparison);
  const prevScoped = useMemo<GoogleAdsFilters>(() => {
    const pr = previousRange({ from: filters.from, to: filters.to });
    return { ...scoped, from: pr.from, to: pr.to };
  }, [scoped, filters.from, filters.to]);
  const prevSummary = useQuery({
    queryKey: ['google-ads-summary-prev', toQueryString(prevScoped)],
    queryFn: () => getGoogleAdsSummary(prevScoped),
    enabled: wantsComparison,
    placeholderData: (prev) => prev,
  });

  const applyPreset = useCallback((p: ReportingDatePreset) => {
    setPreset(p);
    if (p !== 'custom') {
      const range = presetToRange(p, new Date());
      setCustom(range);
      setFilters((f) => ({ ...f, ...range }));
    }
  }, []);

  const applyCustom = useCallback((range: DateRange) => {
    setCustom(range);
    setPreset('custom');
    setFilters((f) => ({ ...f, ...range }));
  }, []);

  const patchFilters = useCallback((patch: Partial<GoogleAdsFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    // Changing a filter invalidates the drill path — the focused campaign may not
    // even be in the new selection, and keeping it would silently contradict the
    // filter bar.
    setFocus(null);
    setFocusNames({});
  }, []);

  const resetFilters = useCallback(() => {
    setFilters((f) => ({ from: f.from, to: f.to }));
    setFocus(null);
    setFocusNames({});
  }, []);

  const drill = useCallback((row: GoogleAdsTreeRow) => {
    const next = focusForRow(row);
    if (!next) return; // an ad is a leaf
    setFocus(next);
    setFocusNames((names) => ({
      ...names,
      ...(row.level === 'location' ? { location: row.name, campaign: null, adGroup: null } : {}),
      ...(row.level === 'campaign' ? { campaign: row.name, adGroup: null } : {}),
      ...(row.level === 'adGroup' ? { adGroup: row.name } : {}),
    }));
  }, []);

  const onSort = useCallback(
    (k: GoogleAdsSortKey) => {
      if (k === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      else {
        setSortKey(k);
        setSortDir('desc');
      }
    },
    [sortKey],
  );

  // Mini-chart series for every KPI tile + the big chart's series, all bucketed
  // by the same grouping so the tiles and the large chart agree bucket-for-bucket.
  // Ratios recompute per bucket and money stays per-currency — see
  // googleTrendSeries for why that has to happen in the adapter rather than in the
  // chart components.
  // The agency display currency, resolved against the LIVE rate table — a stored
  // code the table can no longer reach degrades to Native rather than showing one
  // currency's figures under another's label.
  const fxSnapshot = fx.data?.rates ?? null;
  const displayCurrency = normalizeDisplayCurrency(view.displayCurrency, fxSnapshot);
  const money = useMemo(
    () => ({ display: displayCurrency, snapshot: fxSnapshot }),
    [displayCurrency, fxSnapshot],
  );
  // ⚠️ ONE conversion of the summary block feeds the KPI tiles, the
  // competitiveness panel and the widget scorecards — see googleCurrency for why
  // the METRIC BLOCK is rewritten rather than each formatter patched. Native mode
  // returns it by identity, so this is a no-op there.
  const shownMetrics = useMemo(
    () => convertGoogleMetrics(summary.data?.metrics, displayCurrency, fxSnapshot),
    [summary.data?.metrics, displayCurrency, fxSnapshot],
  );

  const tileSeries = useMemo(() => {
    const out: Record<string, TrendChartData> = {};
    for (const key of view.kpis) {
      out[key] = buildGoogleTrendSeries(summary.data, key, view.grouping, money);
    }
    return out;
  }, [summary.data, view.kpis, view.grouping, money]);

  const bigSeries = useMemo(
    () => buildGoogleTrendSeries(summary.data, view.bigChartMetric, view.grouping, money),
    [summary.data, view.bigChartMetric, view.grouping, money],
  );

  const crumbs = breadcrumbs(focus, focusNames);
  // The big chart follows the drill focus because the summary it plots is already
  // focus-scoped; this just names what the user is looking at.
  const scopeLabel =
    focusNames.adGroup ?? focusNames.campaign ?? focusNames.location ?? 'All locations';
  const health = summary.data?.attributionHealth;
  const gapCount =
    (health?.unattributed.campaigns ?? 0) +
    (health?.collisions.campaigns ?? 0) +
    (health?.unlinked.customers ?? 0);

  // The KPI tiles region. One panel per selected metric, in the KPI picker's
  // order — 3 of 12 columns each, so the packer's row-wrap reproduces exactly
  // the `md:grid-cols-4` row the tiles rendered in before.
  const tilePanels = useMemo<ChartPanel[]>(
    () =>
      view.kpis.map((key) => ({
        key: canvasPanelKey('tile', key),
        title: metricDef(key)?.label ?? key,
        defaultW: 3,
        defaultH: 4,
        // A tile may legitimately go narrower than a chart (6-up), and shorter
        // once its mini-chart is squeezed out.
        minW: 2,
        minH: 3,
        // The grip lives in the tile's own header beside the style toggle — a
        // strip above each tile in a 4-up row would be visual noise.
        providesOwnGrip: true,
        node: (
          <GoogleAdsKpiTile
            metricKey={key}
            metrics={shownMetrics}
            series={tileSeries[key]}
            style={tileStyleFor(view.tileStyles, key)}
            onStyleChange={setTileStyle}
            isActive={key === view.bigChartMetric}
            onSelect={(bigChartMetric) => commitView({ ...view, bigChartMetric })}
            loading={summary.isLoading}
          />
        ),
      })),
    [view, tileSeries, shownMetrics, summary.isLoading, setTileStyle, commitView],
  );

  // The chart panels the grid arranges — exactly the charts the chart menu has
  // turned on, in the order they appear on the page today (trend beside
  // competitiveness, the multi-metric trend below). Passing them in DISPLAY
  // order is what makes the default layout reproduce the current appearance;
  // the catalogue's own order would put multiTrend in between.
  //
  // The single-metric chart is 8 columns beside competitiveness and the full 12
  // without it — again, exactly what the retired CSS grid did.
  const chartPanels = useMemo<ChartPanel[]>(() => {
    const showsCompetitiveness = view.charts.includes('competitiveness');
    const panels: ChartPanel[] = [];
    if (view.charts.includes('trend')) {
      panels.push({
        key: canvasPanelKey('chart', 'trend'),
        title: 'Selected metric',
        defaultW: showsCompetitiveness ? 8 : 12,
        defaultH: 8,
        minH: 5,
        node: (
          <GoogleAdsBigChart
            data={bigSeries}
            style={view.chartStyle}
            onStyleChange={(chartStyle) => commitView({ ...view, chartStyle })}
            scopeLabel={scopeLabel}
            loading={summary.isLoading}
          />
        ),
      });
    }
    if (showsCompetitiveness) {
      panels.push({
        key: canvasPanelKey('chart', 'competitiveness'),
        title: 'Auction competitiveness',
        defaultW: 4,
        // Matches the trend chart's height, as the CSS grid's stretch did.
        defaultH: 8,
        minW: 3,
        minH: 5,
        node: <CompetitivenessBlock metrics={shownMetrics} loading={summary.isLoading} />,
      });
    }
    if (view.charts.includes('multiTrend')) {
      panels.push({
        key: canvasPanelKey('chart', 'multiTrend'),
        title: 'Trend (multi-metric)',
        defaultW: 12,
        defaultH: 9,
        minH: 6,
        // The series picker rides the panel's toolbar slot rather than the body,
        // so the body keeps exactly one child (which is what lets the panel-fill
        // CSS target the chart card unambiguously).
        toolbar: (
          <MetricPicker
            label="Series"
            selected={view.trendMetrics}
            onChange={setTrendMetrics}
            max={4}
          />
        ),
        node: (
          <GoogleAdsTrend
            trend={summary.data?.trend}
            selected={view.trendMetrics}
            loading={summary.isLoading}
            money={money}
          />
        ),
      });
    }
    return panels;
  }, [
    view,
    bigSeries,
    scopeLabel,
    summary.data,
    shownMetrics,
    summary.isLoading,
    commitView,
    setTrendMetrics,
    money,
  ]);

  // The TABLES region — the two secondary tables as panels. Both full width by
  // default, stacked, which is exactly how they read as fixed sections today;
  // Search terms is the taller of the two (it was a 28rem scroll region).
  //
  // Each carries its own Columns picker in its own header, over its OWN
  // vocabulary — see tableWidgets.ts for why those two vocabularies must not be
  // merged, and for why the campaign-type one can never include impression share.
  const tablePanels = useMemo<ChartPanel[]>(() => {
    const panels: ChartPanel[] = [];
    if (view.tables.includes('channelBreakdown')) {
      panels.push({
        key: canvasPanelKey('table', 'channelBreakdown'),
        title: 'Performance by campaign type',
        defaultW: 12,
        defaultH: 8,
        minH: 4,
        node: (
          <ChannelBreakdown
            rows={channels.data?.rows}
            loading={channels.isLoading}
            money={money}
            columns={view.channelColumns}
            onColumnsChange={(channelColumns) => commitView({ ...view, channelColumns })}
          />
        ),
      });
    }
    if (view.tables.includes('searchTerms')) {
      panels.push({
        key: canvasPanelKey('table', 'searchTerms'),
        title: 'Search terms',
        defaultW: 12,
        // ~536px — near enough to the 28rem scroll region plus its header that
        // the default arrangement reproduces today's appearance.
        defaultH: 12,
        minH: 5,
        node: (
          <SearchTermsPanel
            data={terms.data}
            loading={terms.isLoading}
            money={money}
            columns={view.searchTermColumns}
            onColumnsChange={(searchTermColumns) => commitView({ ...view, searchTermColumns })}
          />
        ),
      });
    }
    return panels;
  }, [view, channels.data, channels.isLoading, terms.data, terms.isLoading, commitView, money]);

  // ── The user-added widget region ───────────────────────────────────────────
  // Google's side of the shared framework. Every resolver reuses the paths the
  // KPI tiles already run through — `formatMetric` (per-currency money, "—" for
  // an unmeasured share) and `buildGoogleTrendSeries` — so a widget and a tile of
  // the same metric cannot disagree about the figure.
  const widgetSource = useMemo<WidgetDataSource>(
    () => ({
      metrics: GOOGLE_ADS_METRICS.map((m) => ({ key: m.key, label: m.label, hint: m.hint })),
      number: (metricKey) => {
        const key = metricKey as GoogleAdsMetricKey;
        const def = metricDef(key);
        const metrics = shownMetrics;
        return {
          label: def?.label ?? metricKey,
          value: metrics ? formatMetric(metrics, key) : NOT_MEASURED,
          // An impression share Google didn't report is "—", never a 0 — the
          // same distinction the tiles make, from the same helper.
          unmeasured: metrics ? isMetricUnmeasured(metrics, key) : true,
          tone: googleMetricTone(key),
          // ⚠️ In display mode BOTH periods convert first, so a mixed-currency
          // scope finally GETS a money delta — the refusal was never about
          // mixing, it was about having no single figure to take a % of.
          delta: googleMetricDelta(metrics, prevSummary.data?.metrics, key, money),
        };
      },
      trend: (metricKey) =>
        buildGoogleTrendSeries(summary.data, metricKey as GoogleAdsMetricKey, view.grouping, money),
      style: (metricKey) => tileStyleFor(view.tileStyles, metricKey as GoogleAdsMetricKey),
      onStyleChange: (metricKey, style) =>
        setTileStyle(metricKey as GoogleAdsMetricKey, style),
      comparisonAvailable: true,
      loading: summary.isLoading || (wantsComparison && prevSummary.isLoading),

      // ── Phase 2 ──────────────────────────────────────────────────────────
      // Google's funnel vocabulary. Interactions is deliberately NOT used as the
      // middle stage even though it is the broader denominator: a funnel is read
      // as "how many of the people who saw it clicked", and clicks is the term a
      // client understands.
      funnel: () => {
        const m = shownMetrics;
        return [
          { label: 'Impressions', value: m ? m.impressions : null },
          { label: 'Clicks', value: m ? m.clicks : null },
          { label: 'Conversions', value: m ? m.conversions : null },
        ];
      },
      percentMetrics: GOOGLE_ADS_METRICS.filter((m) => m.kind === 'pct').map((m) => ({
        key: m.key,
        label: m.label,
        hint: m.hint,
      })),
      percent: (metricKey) => {
        const key = metricKey as GoogleAdsMetricKey;
        if (metricDef(key)?.kind !== 'pct') return null;
        // Percentages are currency-agnostic — nothing to convert.
        return shownMetrics ? metricScalar(shownMetrics, key) : null;
      },
      // ⚠️ BOTH DIMENSIONS COME FROM DATA THE PAGE ALREADY FETCHES — the
      // campaign-type breakdown and the explorer tree. No new endpoint, no new
      // query: a dimensional widget costs nothing beyond what is on screen.
      dimensions: GOOGLE_WIDGET_DIMENSIONS,
      dimensional: (metricKey, dimension) =>
        buildGoogleDimensionalData({
          metricKey: metricKey as GoogleAdsMetricKey,
          dimension,
          channels: channels.data?.rows,
          channelsLoading: channels.isLoading,
          tree: tree.data,
          treeLoading: tree.isLoading,
          // ⚠️ Native still REFUSES a money pie across currencies; a display
          // currency converts each entity first and it plots.
          money,
        }),
    }),
    [
      summary.data,
      shownMetrics,
      money,
      summary.isLoading,
      prevSummary.data,
      prevSummary.isLoading,
      wantsComparison,
      view,
      setTileStyle,
      channels.data,
      channels.isLoading,
      tree.data,
      tree.isLoading,
    ],
  );

  const setWidgets = useCallback(
    (widgets: ReportingWidget[]) => commitView({ ...view, widgets }),
    [view, commitView],
  );

  // ── THE CANVAS ─────────────────────────────────────────────────────────────
  // ONE grid for the whole top section — KPI tiles, charts, the two secondary
  // tables and the user's own widgets — so every panel is freely placeable among
  // the others rather than only within its own former region.
  //
  // ⚠️ THE ORDER HERE IS THE DEFAULT ARRANGEMENT. Supplied tiles → charts →
  // tables → widgets at the sizes each used to have, the packer reproduces
  // exactly the stack the four separate regions rendered, so the collapse
  // changes nothing on screen until the user drags something.
  const canvasPanels = useMemo<ChartPanel[]>(
    () => [
      ...tilePanels,
      ...chartPanels,
      ...tablePanels,
      ...buildWidgetPanels({
        widgets: view.widgets,
        source: widgetSource,
        onWidgetsChange: setWidgets,
      }),
    ],
    [tilePanels, chartPanels, tablePanels, view.widgets, widgetSource, setWidgets],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Google Ads"
        description="Performance, auction competitiveness and search terms by location — spend attributed from the Google account it runs under."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={`#${SEARCH_TERMS_ANCHOR}`}>
                <ArrowDownToLine className="mr-1 h-3.5 w-3.5" />
                Search terms
              </a>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void summary.refetch();
                void channels.refetch();
                void tree.refetch();
                void terms.refetch();
              }}
            >
              <RefreshCw className={cn('mr-1 h-3.5 w-3.5', summary.isFetching && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        }
      />

      <GoogleAdsFilterBar
        filters={filters}
        options={options.data}
        preset={preset}
        onPresetChange={applyPreset}
        custom={custom}
        onCustomChange={applyCustom}
        onChange={patchFilters}
        onReset={resetFilters}
      />

      {/* Breadcrumb — the way back up out of a drill. */}
      {crumbs.length > 1 && (
        <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Drill path">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-muted-foreground">›</span>}
              {i === crumbs.length - 1 ? (
                <span className="font-medium">{c.label}</span>
              ) : (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => {
                    setFocus(c.focus);
                    if (!c.focus) setFocusNames({});
                  }}
                >
                  {c.label}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}

      {summary.isError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          Couldn’t load Google Ads data.{' '}
          <span className="text-muted-foreground">
            If this is a fresh environment the `google_ads` view layer may not be built yet — run{' '}
            <code className="rounded bg-muted px-1">npm run google-ads:views</code>.
          </span>
        </div>
      )}

      {/* Attribution gaps. Unattributed spend still counts in the agency total but
          belongs to no brand/AM, so it vanishes the moment a filter is applied —
          saying so beats letting a filtered total quietly come up short. */}
      {gapCount > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <span className="font-medium">Some spend can’t be attributed to a location.</span>{' '}
            <span className="text-muted-foreground">
              {health!.unattributed.campaigns > 0 && (
                <>{health!.unattributed.campaigns} campaign(s) on a shared account match no
                location’s token. </>
              )}
              {health!.collisions.campaigns > 0 && (
                <>{health!.collisions.campaigns} match more than one location. </>
              )}
              {health!.unlinked.customers > 0 && (
                <>
                  {health!.unlinked.customers} Google account(s) aren’t linked to a kyck account
                  {health!.unlinked.names.length > 0 && <> ({health!.unlinked.names.join(', ')})</>}.{' '}
                </>
              )}
              These are excluded from any Brand / Group / AM / Account filter — add a match token on
              the location to resolve them.
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground">
            {summary.data ? `${formatCount(tree.data?.rows.length ?? 0)} rows in scope` : ' '}
          </h2>
          {/* A converted figure must never be mistaken for a native one. */}
          <ConversionNote display={displayCurrency} snapshot={fxSnapshot} />
        </div>
        <div className="flex items-center gap-2">
          {/* The agency display currency — Native (per-currency, the default) or
              convert everything at this month's rate. Same control, same rate
              table and the same query key as the Meta dashboard. */}
          <DisplayCurrencySelect
            value={displayCurrency}
            onChange={(next) => commitView({ ...view, displayCurrency: next })}
            snapshot={fxSnapshot}
            isAdmin={user.role === 'admin'}
          />
          <MetricPicker label="KPIs" selected={view.kpis} onChange={setKpiMetrics} max={8} />
          <GoogleChartsMenu
            selected={view.charts}
            grouping={view.grouping}
            onChange={(charts) => commitView({ ...view, charts })}
            onGroupingChange={(grouping) => commitView({ ...view, grouping })}
          />
          <GoogleTablesMenu
            selected={view.tables}
            onChange={(tables) => commitView({ ...view, tables })}
          />
          {/* The canvas toolbar's last control. It sits with the other menus
              because they all govern WHICH panels exist; the canvas below
              governs where they sit. */}
          <AddWidgetButton
            widgets={view.widgets}
            onWidgetsChange={setWidgets}
            defaultMetricKey="cost"
          />
          {/* The save model: which view you're on, Reset back to the team
              default, and (admins) make this view the team default. */}
          <ReportingViewActions
            gridKey={GOOGLE_ADS_GRID_KEY}
            preferences={encodeGoogleAdsViewState(view)}
            onReset={(next) =>
              setView(decodeGoogleAdsViewState(next.gridPreferences?.[GOOGLE_ADS_GRID_KEY]))
            }
          />
        </div>
      </div>

      {/* ── THE CANVAS ────────────────────────────────────────────────────────
          ONE grid for the whole top section: the KPI tiles (each with its own
          mini-chart and style toggle, each selecting what the big chart plots),
          the three charts, the two secondary tables with their own column
          pickers, and any widgets the user has added. Every panel is freely
          placeable among the others — a single number top-left, the trend beside
          it, the campaign-type table underneath — rather than being confined to
          a region of its own kind. Each panel keeps ALL of its own controls; the
          canvas governs only WHERE things sit, while the menus above govern
          which panels exist.

          ⚠️ The single Reset now lives in the toolbar above
          (ReportingViewActions), not on the grid: since the save model landed it
          no longer means "put the panels back" but "discard my personal view and
          go back to the team default", which belongs beside the label saying
          which view you are on. The explorer tree below stays FIXED — it is the
          page's structural anchor, and a page whose anchor can be dragged away
          reads as broken with no way back. */}
      <ReportingChartGrid
        panels={canvasPanels}
        layout={view.canvasLayout}
        onLayoutChange={(next) =>
          commitView({
            ...view,
            // Merge rather than replace: a panel the user has hidden (a chart or
            // table toggled off, an unselected KPI) keeps its stored slot, so
            // re-enabling it lands where they left it.
            canvasLayout: mergeStoredLayout(
              view.canvasLayout,
              next,
              googleAdsCanvasPanelKeys(view.widgets),
            ),
          })
        }
      />

      {/* ⚠️ Deepening the grouping deepens the leaf grain, and Google does not
          report every campaign type at every grain — a Performance Max campaign
          has no ad groups and no ads at all. Saying so beats letting the table's
          total quietly fall below the KPI tiles. */}
      {tree.data?.excludedByGrain && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <span className="font-medium">
              {tree.data.excludedByGrain.campaigns} campaign
              {tree.data.excludedByGrain.campaigns === 1 ? '' : 's'} (
              {formatCurrencyAmounts(tree.data.excludedByGrain.cost)}) aren’t in this table.
            </span>{' '}
            <span className="text-muted-foreground">
              Google doesn’t report them at{' '}
              {tree.data.excludedByGrain.grain === 'ad' ? 'ad' : 'ad group'} level — Performance
              Max campaigns use asset groups instead. Turn that level off to see them.
              {tree.data.excludedByGrain.names.length > 0 && (
                <> ({tree.data.excludedByGrain.names.slice(0, 3).join(', ')}
                {tree.data.excludedByGrain.names.length > 3 && ' …'})</>
              )}
            </span>
          </div>
        </div>
      )}

      <GoogleAdsTree
        money={money}
        rows={tree.data?.rows}
        levels={tree.data?.levels ?? view.levels}
        metrics={columnMetrics}
        attributes={view.attributes}
        loading={tree.isLoading}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        onDrill={drill}
        // Group-by on the LEFT of the table header, Columns on the RIGHT — the
        // same arrangement the Meta explorer uses.
        header={
          <LevelChips
            levels={view.levels}
            onToggle={onToggleLevel}
            busy={saveView.isPending || tree.isFetching}
          />
        }
        // One picker, two groups: the metric columns and the "Campaign details"
        // attribute columns (off by default).
        actions={
          <MetricPicker
            label="Columns"
            selected={columnMetrics}
            onChange={setColumnMetrics}
            attributes={GOOGLE_ADS_ATTRIBUTES}
            selectedAttributes={view.attributes}
            onAttributesChange={setAttributeColumns}
          />
        }
        // Any focus puts the Location in the breadcrumb above the table, which
        // makes repeating it on every campaign row redundant.
        locationFocused={focus != null}
      />

      {summary.data?.asOf && (
        <p className="pb-4 text-xs text-muted-foreground">
          Data as of {summary.data.asOf} (Google Ads Data Transfer).
        </p>
      )}
    </div>
  );
}
